import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
// Direct import for better tree-shaking
import Bars3Icon from "@heroicons/react/24/outline/Bars3Icon";
import { useAuth } from "../../hooks/useAuth";
import { useLogout } from "../../hooks/useLogout";
import { useProfileStore } from "../../stores/profileStore";
import { useSidebarStore } from "../../stores/sidebarStore.ts";
import logo from "../../../assets/logo.png";

export function Header() {
  const { user } = useAuth();
  const { logout } = useLogout();
  const { profile } = useProfileStore();
  const { toggle: toggleSidebar } = useSidebarStore();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Memoize avatar URL with cache buster to prevent recalculation on every render
  const avatarUrl = useMemo(() => {
    if (!profile?.avatarUrl) return null;
    const timestamp = profile.updatedAt ? new Date(profile.updatedAt).getTime() : 0;
    return `${profile.avatarUrl}?t=${timestamp}`;
  }, [profile?.avatarUrl, profile?.updatedAt]);

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggleSidebar}
          className="md:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          aria-label="Toggle menu"
        >
          <Bars3Icon className="w-6 h-6" />
        </button>
        <img src={logo} alt="RunLens" className="h-10 md:h-12" />
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-2 hover:bg-brand-50 rounded-lg p-2 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 text-sm font-medium overflow-hidden">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              user?.email?.[0].toUpperCase() || "U"
            )}
          </div>
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setIsDropdownOpen(false);
                navigate("/app/settings/profile");
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-brand-50"
            >
              Settings
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
